import { Button } from "@rosette/web_components/src/ui/Button.js";
import { AutoGameEndBanner } from "@/components/game/menu/game_end/GameEndBanner.js";
import type { MutableRefObject } from "react";
import React, { startTransition, useEffect, useState } from "react";
import { AdBanner } from "@/components/game/menu/game_end/AdBanner.js";
import type { UseMutationResult } from "@tanstack/react-query";
import type { StaticImageData } from "next/image";
import Image from "next/image";
import rematchIcon from "@rosette/assets/src/icons/collision_uncropped_icon.png";
import {
    useArePlayersConnected,
    useGameClientControls,
    useGameController,
} from "@/components/game/GameUIContext.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { getBotIcon } from "@/components/icon/getBotIcon.js";
import { BotType } from "@rosette/ts/src/business/game/BotType.js";
import { GameMode } from "@rosette/ts/src/business/game/GameMode.js";
import { writePreferredSettings } from "@rosette/web_utils/src/local/readPreferredSettings.js";
import { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";
import { getRulesetIcon } from "@/components/icon/getRulesetIcon.js";
import type { Path } from "@rosette/ts/src/util/Path.js";
import Link from "next/link";
import { KnownLobbySettings } from "@rosette/ts/src/business/game/KnownLobbySettings.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import { calculateAccuracy, getRatingTargetAccuracy } from "@rosette/ts/src/business/analysis/calculateAccuracy.js";
import { FadingDiv } from "@/components/layout/FadingDiv.js";
import { LIGHT_COLOUR } from "@rosette/web_components/src/analysis/analysisConstants.js";
import { PlayerHeader } from "@/components/game/stats/PlayerHeader.js";
import panda from "@rosette/assets/src/panda.svg";
import type { APIEarnedAchievement, APIGame, APIGameAnalysisOverview } from "@rosette/ts/src/business/api/api_schema.js";
import { HelpButton } from "@/components/generic/popup/HelpButton.js";
import type { LobbyPlayer } from "@rosette/ts/src/business/lobby/LobbyPlayer.js";
import { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import { easeInOut } from "framer-motion";
import { formatRatingChange } from "@rosette/ts/src/util/formatting.js";
import {
    MENU_BUTTON, MenuOptions,
} from "@/components/game/menu/MenuLayout.js";
import { InlineIcon } from "@rosette/web_components/src/ui/InlineIcon.js";


interface PlayerAccuracyBoxProps {
    player: LobbyPlayer;
    yourPlayer: PlayerType | null;
    ruleset: Ruleset | null;
    accuracy: number | null;
    initialRating: number | null;
    ratingChange: number | null;
}


function PlayerAccuracyBox({
    player, yourPlayer, ruleset, accuracy, initialRating, ratingChange,
}: PlayerAccuracyBoxProps) {
    const isYourPlayer = (yourPlayer !== null && yourPlayer === player.getPlayerType());

    let roundedInitialRating: number | null = null;
    let roundedFinalRating: number | null = null;
    let roundedRatingChange: number | null = null;
    let isYourAccuracyAboveRating: boolean = false;
    let isYourAccuracyBelowRating: boolean = false;
    if (initialRating !== null && ratingChange !== null && accuracy !== null) {
        roundedInitialRating = Math.round(initialRating);
        roundedFinalRating = Math.round(initialRating + ratingChange);
        roundedRatingChange = roundedFinalRating - roundedInitialRating;

        if (isYourPlayer) {
            const ratingTargetAccuracy = getRatingTargetAccuracy(accuracy);
            isYourAccuracyAboveRating = (ratingTargetAccuracy > initialRating + 100);
            isYourAccuracyBelowRating = (ratingTargetAccuracy < initialRating - 100);
        }
    }

    const [applyRatingChange, setApplyRatingChange] = useState(0);
    const [finishedAnimating, setFinishedAnimating] = useState(false);
    useEffect(() => {
        if (roundedInitialRating === null || roundedRatingChange === null)
            return;

        if (roundedRatingChange === 0 || !isYourPlayer) {
            setApplyRatingChange(roundedRatingChange);
            setFinishedAnimating(true);
            return;
        }

        setApplyRatingChange(0);
        setFinishedAnimating(false);
        const delayMs = 500;
        const animTimeMs = Math.min(3000, 500 * Math.abs(roundedRatingChange));

        const timers: (number | NodeJS.Timeout)[] = [];
        const noTimers = Math.round(animTimeMs / 33.3);
        const intervalMs = animTimeMs / noTimers;

        let lastApplyRatingChange = 0;
        for (let index = 0; index < noTimers; index++) {
            const progress = (index + 1) / noTimers;
            const proportion = easeInOut(progress);
            const applyRatingChange = Math.round(roundedRatingChange * proportion);
            if (applyRatingChange === lastApplyRatingChange)
                continue;

            lastApplyRatingChange = applyRatingChange;
            timers.push(setTimeout(
                () => setApplyRatingChange(applyRatingChange),
                delayMs + intervalMs * index,
            ));
        }
        timers.push(setTimeout(
            () => setFinishedAnimating(true),
            delayMs + animTimeMs,
        ));

        return () => {
            for (const timer of timers) {
                clearTimeout(timer);
            }
        };
    }, [isYourPlayer, roundedInitialRating, roundedRatingChange, setApplyRatingChange]);

    let ratingStr: string | null = null;
    let ratingChangeStr: string | null = null;
    const isRatingIncrease = (ratingChange !== null && ratingChange > 0);
    if (roundedInitialRating !== null && roundedRatingChange !== null) {
        ratingStr = `${(roundedInitialRating + applyRatingChange).toFixed(0)}`;
        ratingChangeStr = formatRatingChange(roundedRatingChange);
    }

    const ratingChangeColour = (isRatingIncrease ? "text-primary" : "text-danger");
    return (
        <div
            className="flex flex-col flex-1 p-2 pt-1 overflow-hidden bg-page rounded-xl"
            style={{ boxShadow: `0 0 2px ${LIGHT_COLOUR}` }}>

            <div className="flex flex-row items-start w-full text-base">
                <PlayerHeader
                    className="max-w-[85%] overflow-hidden mr-auto"
                    player={player}
                    yourPlayer={yourPlayer}
                    small={true} />

                {accuracy && ratingStr === null && (
                    <HelpButton className="mt-1">
                        <p className="m-0">
                            Your accuracy is a measure of how well you played.
                            Each mistake causes your accuracy to fall. Read more
                            about this on
                            {" "}
                            <Link href="/review" target="_blank">
                                our game review page.
                            </Link>
                        </p>
                    </HelpButton>
                )}

                {ratingStr !== null && (
                    <HelpButton className="mt-1">
                        <p className="m-0">
                            Your rating will increase when you win, and decrease
                            when you lose. The amount of rating you gain or lose
                            depends on your accuracy and some other factors. Read more
                            about this on
                            {" "}
                            <Link href="/review" target="_blank">
                                our game review page.
                            </Link>
                        </p>
                    </HelpButton>
                )}
            </div>

            {ratingStr !== null && ratingChangeStr !== null && (
                <p className="m-0 font-bold text-base">
                    <span className={(
                        yourPlayer === player.getPlayerType() && !finishedAnimating
                            ? "text-emphasis"
                            : ratingChangeColour
                    )}>

                        {ratingStr}
                        {" rating"}
                    </span>
                    {finishedAnimating && (
                        <>
                            {"  "}
                            <span className={ratingChangeColour}>

                                {ratingChangeStr}
                            </span>
                        </>
                    )}
                </p>
            )}

            {accuracy && (
                <div className="mb-3">
                    <p className={cn(
                        "m-0 font-bold text-base",
                        isYourAccuracyAboveRating ? "text-success" : null,
                        isYourAccuracyBelowRating ? "text-danger" : null,
                    )}>
                        {`${Math.floor(accuracy)}% accuracy`}
                    </p>
                </div>
            )}

            {ruleset && (
                <p className="m-0 text-base">
                    <InlineIcon>
                        <Image
                            src={getRulesetIcon(ruleset)}
                            alt={ruleset.getDesc() ?? "Unknown Ruleset"} />
                    </InlineIcon>

                    {"  "}
                    {`${ruleset.getName()} Rules`}
                </p>
            )}
        </div>
    );
}


interface WinMenuProps {
    apiGame: APIGame | null | undefined;
    analysisOverview: APIGameAnalysisOverview | null | undefined;
    contentRef: MutableRefObject<HTMLDivElement | null>;
    uploadGame: UseMutationResult<APIEarnedAchievement[], Error, void, unknown>;
    isMenuActive: boolean;
    continuePath: Path | null;
}


export function WinMenu({
    apiGame, analysisOverview,
    contentRef, uploadGame, isMenuActive,
    continuePath,
}: WinMenuProps) {
    const clientControls = useGameClientControls();
    const controller = useGameController();
    const currentSettings = controller.getGameSource().getSetupSettings();

    const players = useRune(controller.getPlayers());
    const lobbyStatus = useRune(controller.getLobbyStatus());
    const { p1Connected, p2Connected } = useArePlayersConnected(lobbyStatus, players);
    const bothPlayersConnected = (p1Connected && p2Connected);
    const rematchRequestedBy = lobbyStatus.getRematchRequestedBy();
    const yourPlayerNo = players.getYourPlayerNo();
    const youRequestedRematch = (rematchRequestedBy && rematchRequestedBy === yourPlayerNo);
    const isSpectator = (lobbyStatus.isRemote() && yourPlayerNo === null);

    const [analysisEverUnavailable] = useState(!analysisOverview);
    let ruleset: Ruleset | null = null;
    let lightAccuracy: number | null = null;
    let darkAccuracy: number | null = null;
    let lightInitialRating: number | null = null;
    let darkInitialRating: number | null = null;
    let lightRatingChange: number | null = null;
    let darkRatingChange: number | null = null;
    if (apiGame && analysisOverview) {
        ruleset = Ruleset.getByID(apiGame.rulesetID);
        lightAccuracy = calculateAccuracy(ruleset, analysisOverview.lightMeanMoveDelta);
        darkAccuracy = calculateAccuracy(ruleset, analysisOverview.darkMeanMoveDelta);
        lightInitialRating = analysisOverview.lightInitialRating;
        darkInitialRating = analysisOverview.darkInitialRating;
        lightRatingChange = analysisOverview.lightRatingChange;
        darkRatingChange = analysisOverview.darkRatingChange;
    }

    // Suggest a new difficulty or ruleset.
    let nextSettings: KnownLobbySettings | null = null;
    let nextSettingsLabel: string | null = null;
    let nextSettingsIcon: StaticImageData | null = null;
    let nextSettingsIconAlt: string | null = null;
    if (currentSettings instanceof KnownLobbySettings
        && currentSettings.getMode() === GameMode.COMPUTER) {

        const ruleType = currentSettings.getRuleset();
        const difficulty = currentSettings.getBotType();
        if (difficulty === BotType.EASY || difficulty === BotType.MEDIUM) {
            nextSettings = currentSettings.withBotType(
                difficulty === BotType.EASY ? BotType.MEDIUM : BotType.HARD,
            );
            nextSettingsLabel = `Play vs ${nextSettings.getBotType().getName()}`;
            nextSettingsIcon = getBotIcon(nextSettings.getBotType());
            nextSettingsIconAlt = nextSettings.getBotType().getDesc();

        } else if (difficulty === BotType.HARD || difficulty === BotType.PANDA) {
            if (ruleType.getName() === Ruleset.FINKEL.getName()) {
                nextSettings = currentSettings.withRuleset(Ruleset.BLITZ);
            } else if (ruleType.getName() === Ruleset.BLITZ.getName()) {
                nextSettings = currentSettings.withRuleset(Ruleset.MASTERS);
            } else {
                nextSettings = currentSettings.withRuleset(Ruleset.FINKEL);
            }
            nextSettingsLabel = `Try ${nextSettings.getRuleset().getName()} Rules`;
            nextSettingsIcon = getRulesetIcon(nextSettings.getRuleset());
            nextSettingsIconAlt = nextSettings.getRuleset().getDesc();
        }
    }

    const showUploadGameButton = (uploadGame.isPending || uploadGame.isError);
    const saveError = uploadGame.error?.message;
    return (
        <MenuOptions contentRef={contentRef}>
            <div className="flex flex-col gap-3 w-full">
                <AutoGameEndBanner visible={isMenuActive} />

                {analysisOverview && (
                    <FadingDiv
                        className={cn(
                            "flex flex-wrap items-start gap-3 w-full px-6",
                            players.getYourPlayer() === PlayerType.DARK ? "flex-row-reverse" : "flex-row",
                        )}
                        options={{ animateHeight: analysisEverUnavailable }}>

                        <PlayerAccuracyBox
                            player={players.getLightPlayer()}
                            yourPlayer={players.getYourPlayer()}
                            ruleset={ruleset}
                            accuracy={lightAccuracy}
                            initialRating={lightInitialRating}
                            ratingChange={lightRatingChange} />

                        <PlayerAccuracyBox
                            player={players.getDarkPlayer()}
                            yourPlayer={players.getYourPlayer()}
                            ruleset={ruleset}
                            accuracy={darkAccuracy}
                            initialRating={darkInitialRating}
                            ratingChange={darkRatingChange} />
                    </FadingDiv>
                )}
            </div>

            {showUploadGameButton && (
                <Button
                    size="lg"
                    className={MENU_BUTTON}
                    onClick={() => uploadGame.mutate()}>
                    {uploadGame.error
                        ? `Failed to save game${saveError ? ": " + saveError : ""}. Retry?`
                        : "Saving game..."}
                </Button>
            )}

            {!isSpectator && !showUploadGameButton && bothPlayersConnected && (
                <Button
                    tone={rematchRequestedBy ? "success" : "neutral"}
                    size="lg"
                    className={cn(
                        MENU_BUTTON,
                        "animate-breathe motion-reduce:animate-none",
                        rematchRequestedBy ? "[animation-duration:2s]" : null,
                    )}
                    onClick={() => {
                        startTransition(() => {
                            controller.rematch();
                        });
                    }}>

                    <InlineIcon size="lg" className="mr-2">
                        <Image
                            src={rematchIcon}
                            alt="Two game pieces colliding and sparks flying" />
                    </InlineIcon>

                    {"Rematch" + (youRequestedRematch ? "!" : "?")}
                </Button>
            )}

            {!isSpectator && !showUploadGameButton && !bothPlayersConnected && (
                <Button
                    size="lg"
                    className={MENU_BUTTON}
                    disabled={true}>

                    Opponent Left
                </Button>
            )}

            {nextSettings && (
                <Button
                    size="lg"
                    className={MENU_BUTTON}
                    onClick={() => {
                        startTransition(() => {
                            if (nextSettings) {
                                writePreferredSettings(nextSettings);
                                clientControls.startGame(nextSettings);
                            }
                        });
                    }}>

                    {nextSettingsIcon && nextSettingsIconAlt && (
                        <InlineIcon size="lg" className="mr-2">
                            <Image
                                src={nextSettingsIcon}
                                alt={nextSettingsIconAlt} />
                        </InlineIcon>
                    )}

                    {nextSettingsLabel}
                </Button>
            )}

            {continuePath && (
                <Button
                    size="lg"
                    className={MENU_BUTTON}
                    href={continuePath.toString()}>
                    <InlineIcon size="lg" className="mr-2">
                        <Image
                            src={panda}
                            alt="The Panda bot" />
                    </InlineIcon>
                    {" "}
                    <span className="align-middle">
                        Review Game
                    </span>
                </Button>
            )}

            <Button
                size="lg"
                className={MENU_BUTTON}
                href="/">
                <span className="align-middle">
                    Back to Home
                </span>
            </Button>

            <AdBanner />
        </MenuOptions>
    );
}
